import React, { useEffect, useMemo, useRef, useState } from "react";
import BossQuestCard from "./BossQuestCard";
import RewardsCard from "./RewardsCard";
import AnalyticsCard from "./AnalyticsCard";
import { useBossQuest } from "../hooks/useBossQuest";
import { getBossQuestPresentation } from "../utils/bossQuestUtils";
import { useRewards } from "../hooks/useRewards";
import { useAnalytics } from "../hooks/useAnalytics";
import { getDatePartsInTimeZone } from "../utils/timezoneUtils";
import { playSystemSound } from "../audio/soundManager";

const TERMINALS = {
  RAID: "raid",
  REWARDS: "rewards",
  INTEL: "intel",
};
const MOBILE_MEDIA_QUERY = "(max-width: 768px)";

const SystemTerminals = ({ user, userProfile, onProfileUpdate }) => {
  const { bossQuest } = useBossQuest(user?.uid, {
    onProfileUpdate,
    timeZone: userProfile?.timeZone || null,
  });
  const bossQuestView = useMemo(
    () => (bossQuest ? getBossQuestPresentation(bossQuest) : null),
    [bossQuest]
  );
  const { rewards } = useRewards(user?.uid);
  const refreshKey =
    userProfile?.updatedAt || `${userProfile?.totalXP || 0}:${userProfile?.level || 1}`;
  const { analytics } = useAnalytics(user?.uid, refreshKey, userProfile?.timeZone || null);

  const [expandedTerminal, setExpandedTerminal] = useState(null);
  const [drawerHeight, setDrawerHeight] = useState(0);
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
      : false
  );
  const drawerInnerRef = useRef(null);
  const autoExpandResolvedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const applyLayout = (event) => {
      setIsMobileLayout(event.matches);
    };

    setIsMobileLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", applyLayout);
      return () => mediaQuery.removeEventListener("change", applyLayout);
    }

    mediaQuery.addListener(applyLayout);
    return () => mediaQuery.removeListener(applyLayout);
  }, []);

  useEffect(() => {
    if (autoExpandResolvedRef.current) return;

    const weekday = getDatePartsInTimeZone(userProfile?.timeZone || "UTC").weekday;
    const isLateWeek = ["Thu", "Fri", "Sat", "Sun"].includes(weekday);
    const hasPriorityRaid =
      isLateWeek &&
      bossQuest &&
      bossQuest.claimed !== true &&
      bossQuest.status !== "completed";

    if (hasPriorityRaid) {
      setExpandedTerminal(TERMINALS.RAID);
    }

    if (bossQuest || rewards.length || analytics.totalQuests || analytics.totalXP) {
      autoExpandResolvedRef.current = true;
    }
  }, [analytics.totalQuests, analytics.totalXP, bossQuest, rewards.length, userProfile?.timeZone]);

  useEffect(() => {
    if (!expandedTerminal) {
      setDrawerHeight(0);
      return;
    }

    const updateDrawerHeight = () => {
      setDrawerHeight(drawerInnerRef.current?.scrollHeight || 0);
    };

    const frameId = window.requestAnimationFrame(updateDrawerHeight);
    const currentInner = drawerInnerRef.current;
    let resizeObserver = null;

    if (currentInner && typeof window.ResizeObserver === "function") {
      resizeObserver = new window.ResizeObserver(() => {
        updateDrawerHeight();
      });
      resizeObserver.observe(currentInner);
    }

    window.addEventListener("resize", updateDrawerHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateDrawerHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [expandedTerminal, isMobileLayout, bossQuest, rewards.length, analytics.totalQuests, analytics.completionRate7d]);

  const rewardCounts = useMemo(() => {
    const currentLevel = Number(userProfile?.level || 0);
    const unlockedPending = rewards.filter(
      (reward) =>
        !reward.claimed &&
        (!reward.milestoneLevel || currentLevel >= Number(reward.milestoneLevel))
    ).length;
    const locked = rewards.filter(
      (reward) =>
        reward.milestoneLevel && currentLevel < Number(reward.milestoneLevel)
    ).length;
    return { unlockedPending, locked };
  }, [rewards, userProfile?.level]);

  const raidObjectivesRemaining = bossQuestView?.checklist?.length
    ? bossQuestView.checklist.length
    : bossQuest
      ? 1
      : 0;

  const terminalMeta = {
    [TERMINALS.RAID]: {
      label: "[ WEEKLY RAID ]",
      blurb: bossQuest
        ? bossQuest.claimed || bossQuest.status === "completed"
          ? "Boss defeated this week"
          : `Active - ${raidObjectivesRemaining} objectives remain`
        : "No raid assigned",
      render: () => <BossQuestCard user={user} userProfile={userProfile} onProfileUpdate={onProfileUpdate} />,
    },
    [TERMINALS.REWARDS]: {
      label: "[ SYSTEM REWARDS ]",
      blurb:
        rewards.length === 0
          ? "No rewards defined"
          : rewardCounts.unlockedPending > 0
            ? `${rewardCounts.unlockedPending} rewards pending`
            : rewardCounts.locked > 0
              ? `${rewardCounts.locked} rewards locked`
              : "All rewards claimed",
      render: () => <RewardsCard user={user} userProfile={userProfile} />,
    },
    [TERMINALS.INTEL]: {
      label: "[ INTEL FEED ]",
      blurb:
        analytics.totalQuests > 0
          ? `Streak: ${analytics.streak} days - Completion ${analytics.completionRate7d}%`
          : "No data yet",
      render: () => <AnalyticsCard user={user} userProfile={userProfile} />,
    },
  };

  const renderDrawer = (terminalKey) => (
    <div
      className={`system-terminals__drawer ${expandedTerminal === terminalKey ? "system-terminals__drawer--open" : ""}`}
      style={{ maxHeight: expandedTerminal === terminalKey ? `${drawerHeight}px` : "0px" }}
    >
      <div ref={drawerInnerRef} className="system-terminals__drawer-inner">
        {expandedTerminal === terminalKey ? terminalMeta[terminalKey].render() : null}
      </div>
    </div>
  );

  return (
    <section className="system-terminals">
      <div className="system-terminals__label">[ SYSTEM TERMINALS ]</div>

      <div className="system-terminals__grid">
        {Object.entries(terminalMeta).map(([terminalKey, terminal]) => {
          const isOpen = expandedTerminal === terminalKey;
          return (
            <React.Fragment key={terminalKey}>
              <article
                className={`system-terminal-tile ${isOpen ? "system-terminal-tile--open" : ""}`}
              >
                <div className="system-terminal-tile__label">{terminal.label}</div>
                <div className="system-terminal-tile__blurb">{terminal.blurb}</div>
                <button
                  type="button"
                  className="logout-btn system-terminal-tile__button"
                  onClick={() => {
                    playSystemSound("questOpen");
                    setExpandedTerminal((current) =>
                      current === terminalKey ? null : terminalKey
                    );
                  }}
                >
                  {isOpen ? "[ COLLAPSE TERMINAL ]" : "[ OPEN TERMINAL ]"}
                </button>
              </article>
              {isMobileLayout && isOpen ? renderDrawer(terminalKey) : null}
            </React.Fragment>
          );
        })}
      </div>

      {!isMobileLayout && expandedTerminal ? renderDrawer(expandedTerminal) : null}
    </section>
  );
};

export default SystemTerminals;
